// Version configuration and management
export interface VersionInfo {
  version: string;
  buildNumber: string;
  buildDate: string;
  commitHash?: string;
  environment: 'development' | 'staging' | 'production';
  features: string[];
  changelog: string[];
}

// Get version from package.json (injected by Vite)
const packageVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
const buildNumber = import.meta.env.VITE_BUILD_NUMBER || Date.now().toString();
const commitHash = import.meta.env.VITE_COMMIT_HASH || 'unknown';
const environment = (import.meta.env.VITE_ENVIRONMENT as 'development' | 'staging' | 'production') || 'development';

export const APP_VERSION: VersionInfo = {
  version: packageVersion,
  buildNumber,
  buildDate: new Date().toISOString(),
  commitHash,
  environment,
  features: [
    'Interactive Spinner',
    'Multiple Game Modes',
    'Multi-language Support',
    'Dark/Light Theme',
    'Share Functionality',
    'Help Center'
  ],
  changelog: [
    'v1.0.0 - Initial release with core spinner functionality',
    'v1.1.0 - Added game modes (Trivia, Would You Rather, etc.)',
    'v1.2.0 - Implemented multi-language support',
    'v1.3.0 - Enhanced user management and security',
    'v1.4.0 - Enhanced UI with dark/light themes',
    'v1.5.0 - Added help center and documentation'
  ]
};

// Version comparison utilities
export const compareVersions = (version1: string, version2: string): number => {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);
  
  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1 = v1Parts[i] || 0;
    const v2 = v2Parts[i] || 0;
    
    if (v1 > v2) return 1;
    if (v1 < v2) return -1;
  }
  
  return 0;
};

// Check if update is available
export const isUpdateAvailable = (currentVersion: string, latestVersion: string): boolean => {
  return compareVersions(latestVersion, currentVersion) > 0;
};

// Get version display string
export const getVersionDisplay = (): string => {
  return `${APP_VERSION.version} (${APP_VERSION.buildNumber})`;
};

// Get environment-specific version info
export const getEnvironmentInfo = (): string => {
  if (APP_VERSION.environment === 'production') {
    return `v${APP_VERSION.version}`;
  }
  return `v${APP_VERSION.version}-${APP_VERSION.environment} (${APP_VERSION.buildNumber})`;
};

// Version storage keys
export const VERSION_KEYS = {
  CURRENT_VERSION: 'app_current_version',
  LAST_UPDATE_CHECK: 'app_last_update_check',
  UPDATE_NOTIFICATION_DISMISSED: 'app_update_notification_dismissed'
} as const;

// Version management utilities
export const versionUtils = {
  // Save current version to localStorage
  saveCurrentVersion: (): void => {
    localStorage.setItem(VERSION_KEYS.CURRENT_VERSION, APP_VERSION.version);
    localStorage.setItem(VERSION_KEYS.LAST_UPDATE_CHECK, Date.now().toString());
  },

  // Get saved version from localStorage
  getSavedVersion: (): string | null => {
    return localStorage.getItem(VERSION_KEYS.CURRENT_VERSION);
  },

  // Check if this is a new version
  isNewVersion: (): boolean => {
    const savedVersion = versionUtils.getSavedVersion();
    if (!savedVersion) return true; // First time user
    
    return compareVersions(APP_VERSION.version, savedVersion) > 0;
  },

  // Dismiss update notification
  dismissUpdateNotification: (): void => {
    localStorage.setItem(VERSION_KEYS.UPDATE_NOTIFICATION_DISMISSED, Date.now().toString());
  },

  // Check if update notification was dismissed recently (within 24 hours)
  isUpdateNotificationDismissed: (): boolean => {
    const dismissed = localStorage.getItem(VERSION_KEYS.UPDATE_NOTIFICATION_DISMISSED);
    if (!dismissed) return false;
    
    const dismissedTime = parseInt(dismissed);
    const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
    
    return hoursSinceDismissed < 24;
  }
}; 